/**
 * FormRenderer — unified form renderer for designer and App modes.
 *
 * Single source of truth for rendering a FormNode tree from a FormSchema.
 * Used by:
 *   - Canvas (design mode)       — mode='canvas', no real data, selection overlay on top
 *   - Preview (design mode)      — mode='preview', mock data in fields
 *   - Runtime (App mode)         — mode='app', real data, CRUD actions, validation
 *
 * Extension points (callbacks):
 *   renderField      — controls InputField content (mock | controller | placeholder)
 *   renderTable      — controls Table content (placeholder | mock rows | real DataGrid)
 *   onAction         — CommandBar / Button actions (submit, close, custom)
 *
 * The layout engine (FormGrid + row/col positioning) is identical in all modes.
 * Changes to this component automatically propagate to every consumer.
 */
import React, { Fragment, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import type { FormNode, FormSchema } from '../../designer/types/form.types'
import { FormGrid } from '../../designer/components/editors/FormEditor/canvas/FormGrid'
import { isFullWidth } from '../../designer/components/editors/FormEditor/layout/flowLayout'
import { GridColumnsContext } from '../../designer/components/editors/FormEditor/canvas/gridColumnsContext'
import { GroupElement } from '../../designer/components/editors/FormEditor/elements/GroupElement'
import { LabelElement } from '../../designer/components/editors/FormEditor/elements/LabelElement'
import { InputFieldElement } from '../../designer/components/editors/FormEditor/elements/InputFieldElement'
import { TableElement } from '../../designer/components/editors/FormEditor/elements/TableElement'
import { CommandBarElement, type CommandBarExtras } from '../../designer/components/editors/FormEditor/elements/CommandBarElement'
import { ButtonElement } from '../../designer/components/editors/FormEditor/elements/ButtonElement'
import { PictureElement } from '../../designer/components/editors/FormEditor/elements/PictureElement'
import { PagesElement } from './PagesElement'
import { wrapSiblingTablesAsPages } from '@/shared/form/pagesModel'
import { TotalsFooter } from './TotalsFooter'
import { resolveInputFieldAttrName } from './resolveInputFieldAttrName'
import {
  resolveRuntimeFormButtonAction,
  type RuntimeFormDialogActions,
} from '../../app/components/runtimeFormActions'
import {
  resolveRuntimeFieldEnabled,
  resolveRuntimeFieldVisible,
} from '@/shared/hooks/useRuntimeFieldVisibility'
import type { ListFormRuntimeActions } from './listFormActions'

export type FormRenderMode = 'canvas' | 'preview' | 'app'

/**
 * Optional callbacks that override default rendering per element type.
 * When omitted, sensible defaults are used for each mode.
 */
export interface FormRendererCallbacks {
  /** Replaces the default InputField rendering (e.g. with react-hook-form Controller in app mode) */
  renderField?: (
    attrName: string,
    node: FormNode,
    options: { disabled: boolean; mode: FormRenderMode },
  ) => React.ReactNode
  /** Replaces the default Table rendering (e.g. with real DataGrid in app mode) */
  renderTable?: (
    tableName: string,
    node: FormNode,
    options: { disabled: boolean; mode: FormRenderMode },
  ) => React.ReactNode
  /**
   * Wrap every rendered node with additional chrome (used by canvas for selection /
   * resize / drag handles / delete button / context-menu overlay).
   * Returns the wrapped element or the original element unchanged.
   */
  wrapNode?: (node: FormNode, rendered: React.ReactNode) => React.ReactNode
  /**
   * Drop-zone renderer placed between Group children (canvas drag-and-drop).
   * Invoked only when provided; otherwise Group renders without drop zones.
   */
  renderGroupDropZone?: (groupId: string, insertIndex: number) => React.ReactNode
  /** Called when the user clicks a Group's collapse toggle (canvas only). */
  onGroupCollapseToggle?: (nodeId: string) => void
  /**
   * Drop-zone renderer placed between root-level rows (canvas drag-and-drop).
   * Invoked only when provided.
   */
  renderRootDropZone?: (insertIndex: number) => React.ReactNode
  /** Tabular row counts keyed by Page name — badge when a page owns a single Table. */
  pageRowCounts?: Record<string, number>
}

export interface FormRendererProps {
  /** Full schema OR just the root nodes (for backward compat) */
  schema?: FormSchema
  root?: FormNode[]
  /** Render mode — drives default behaviour of fields, tables, buttons */
  mode?: FormRenderMode
  /** Customisation hooks */
  callbacks?: FormRendererCallbacks
  /** Command bar / Button actions (submit / close). When omitted buttons are disabled. */
  formActions?: RuntimeFormDialogActions
  /** Current form values — drives visibleCondition / enabledCondition evaluation */
  formValues?: Record<string, unknown>
  /** Attribute type lookup for InputField default rendering (boolean → checkbox, etc.) */
  attributeTypes?: Record<string, string>
  /**
   * List forms: wire standard CommandBar button names (Create/Edit/Delete) to listActions.
   */
  enableListCrud?: boolean
  /** List form CRUD handlers — used when enableListCrud and mode=app. */
  listActions?: ListFormRuntimeActions
  /**
   * Designer canvas: show full CommandBar from schema for layout editing.
   * Default: true when mode === 'canvas'.
   */
  isDesignerCanvas?: boolean
  /**
   * App mode only: runtime commands the schema can't express (Print, Delete, ...),
   * appended to the schema's OWN root CommandBar. Ignored outside mode='app' and
   * for any non-root CommandBar node (nested/footer bars stay schema-only).
   */
  commandBarExtras?: CommandBarExtras
  /**
   * App record forms: render content at natural height so an ancestor scroll
   * container can page-scroll the whole form (the shell keeps title / commands
   * pinned). Suppresses the fixed-frame layout that otherwise makes the tabular
   * grid the sole scroll host. Default: false.
   */
  pageScroll?: boolean
}

export { resolveInputFieldAttrName }

/**
 * Default renderer for InputField — uses the live MUI input component from the designer package.
 */
function DefaultInputField({ node, mode, attributeTypes }: { node: FormNode; mode: FormRenderMode; attributeTypes?: Record<string, string> }) {
  const attrName = resolveInputFieldAttrName(node) ?? ''
  const attrType = attributeTypes?.[attrName]
  return <InputFieldElement node={node} preview={mode !== 'canvas'} attributeType={attrType} />
}

/**
 * Default renderer for Table — uses the live MUI table from the designer package
 * (column headers + placeholder rows). In app mode consumers should pass a `renderTable`
 * callback to inject a real DataGrid with entity data.
 */
function DefaultTable({ node, mode }: { node: FormNode; mode: FormRenderMode }) {
  return <TableElement node={node} preview={mode !== 'canvas'} />
}

function nodeTreeHasTable(nodes: FormNode[]): boolean {
  return nodes.some(
    (n) => n.type === 'Table' || (n.children != null && n.children.length > 0 && nodeTreeHasTable(n.children)),
  )
}

export function FormRenderer({
  schema,
  root: rootProp,
  mode = 'app',
  callbacks,
  formActions,
  formValues,
  attributeTypes,
  enableListCrud = false,
  listActions,
  isDesignerCanvas: isDesignerCanvasProp,
  commandBarExtras,
  pageScroll = false,
}: FormRendererProps) {
  const root = rootProp ?? schema?.root ?? []
  const layoutRoot = useMemo(
    () => (mode === 'canvas' ? root : wrapSiblingTablesAsPages(root)),
    [mode, root],
  )
  const lockFormScroll = !pageScroll && mode === 'app' && nodeTreeHasTable(layoutRoot)
  const maxContentWidth = schema?.presentation?.maxContentWidth
  /** Narrow forms: stacked form-cards + tighter row gap (presentation.maxContentWidth). */
  const denseRootStack = maxContentWidth != null && maxContentWidth > 0
  /** Cap width only in designer preview/canvas (HTML mock); App mode stretches full dialog width. */
  const capContentWidth = denseRootStack && mode !== 'app'

  const effectiveDesignerCanvas = isDesignerCanvasProp ?? mode === 'canvas'
  const activeListActions = enableListCrud && mode === 'app' && !effectiveDesignerCanvas ? listActions : undefined

  const barRuntime = useMemo(
    () =>
      formActions
        ? {
            resolveAction: resolveRuntimeFormButtonAction,
            submit: formActions.submit,
            save: formActions.save,
            close: formActions.close,
            disabled: formActions.disabled,
          }
        : undefined,
    [formActions],
  )

  const renderNode = useCallback(
    (node: FormNode, inheritedDisabled = false): React.ReactNode => {
      if (!resolveRuntimeFieldVisible(
        { visible: node.visible, visibleCondition: node.visibleCondition },
        formValues,
      )) {
        return null
      }
      const nodeDisabled =
        inheritedDisabled ||
        !resolveRuntimeFieldEnabled(
          { enabled: node.enabled, enabledCondition: node.enabledCondition },
          formValues,
        )

      let rendered: React.ReactNode = null

      switch (node.type) {
        case 'Group':
          rendered = (
            <Box sx={{ width: '100%', opacity: nodeDisabled ? 0.55 : 1 }}>
              <GroupElement
                node={node}
                renderNode={(child) => renderNode(child, nodeDisabled)}
                preview={mode !== 'canvas'}
                renderDropZone={
                  callbacks?.renderGroupDropZone
                    ? (i) => callbacks.renderGroupDropZone!(node.id, i)
                    : undefined
                }
                onCollapseToggle={callbacks?.onGroupCollapseToggle}
              />
            </Box>
          )
          break

        case 'Label':
          rendered = (
            <Box sx={{ opacity: nodeDisabled ? 0.55 : 1 }}>
              <LabelElement node={node} preview={mode !== 'canvas'} />
            </Box>
          )
          break

        case 'InputField': {
          const attrName = resolveInputFieldAttrName(node)
          if (callbacks?.renderField && attrName) {
            rendered = <>{callbacks.renderField(attrName, node, { disabled: nodeDisabled, mode })}</>
          } else {
            rendered = <DefaultInputField node={node} mode={mode} attributeTypes={attributeTypes} />
          }
          break
        }

        case 'Table': {
          const name = node.name || node.title
          if (callbacks?.renderTable && name) {
            rendered = (
              <Box
                sx={{
                  width: '100%',
                  ...(mode === 'app'
                    ? {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }
                    : null),
                }}
              >
                {callbacks.renderTable(name, node, { disabled: nodeDisabled, mode })}
              </Box>
            )
          } else {
            rendered = <DefaultTable node={node} mode={mode} />
          }
          break
        }

        case 'CommandBar': {
          if (mode === 'app' && !barRuntime && !activeListActions) {
            rendered = null
            break
          }
          // Extras (Print/Delete) only ride the layout's ROOT CommandBar — a nested/footer
          // bar (e.g. a tabular section's "Add row" bar) must stay schema-only.
          // Compare against `layoutRoot`, not `root`: wrapSiblingTablesAsPages clones
          // any node with children (CommandBar always has buttons), so identity
          // against the pre-wrap tree is always false and Print/Delete vanish.
          const isRootNode = layoutRoot.includes(node)
          rendered = (
            <CommandBarElement
              node={node}
              preview={mode !== 'canvas'}
              runtimeActions={
                barRuntime
                  ? { ...barRuntime, disabled: (barRuntime.disabled ?? false) || nodeDisabled }
                  : undefined
              }
              listActions={
                activeListActions
                  ? { ...activeListActions, disabled: (activeListActions.disabled ?? false) || nodeDisabled }
                  : undefined
              }
              extraCommands={mode === 'app' && isRootNode ? commandBarExtras : undefined}
            />
          )
          break
        }

        case 'Button':
          rendered = (
            <ButtonElement
              node={node}
              preview={mode !== 'canvas'}
              runtimeActions={
                barRuntime
                  ? { ...barRuntime, disabled: (barRuntime.disabled ?? false) || nodeDisabled }
                  : undefined
              }
              listActions={
                activeListActions
                  ? { ...activeListActions, disabled: (activeListActions.disabled ?? false) || nodeDisabled }
                  : undefined
              }
            />
          )
          break

        case 'Picture':
          rendered = (
            <Box sx={{ opacity: nodeDisabled ? 0.55 : 1 }}>
              <PictureElement node={node} />
            </Box>
          )
          break

        case 'Pages':
          rendered = (
            <PagesElement
              node={node}
              renderNode={(child) => renderNode(child, nodeDisabled)}
              rowCounts={callbacks?.pageRowCounts}
              formValues={formValues}
            />
          )
          break

        case 'Page': {
          const kids = (node.children ?? []).map((child) =>
            child.type === 'Table' ? { ...child, titleVisible: false } : child,
          )
          // Pages that only stack tables keep the plain stack (app scroll host); anything
          // narrower than a row is laid out on the grid like a group, widths included.
          rendered = mode === 'canvas' || kids.some((child) => !isFullWidth(child)) ? (
            <FormGrid
              nodes={kids}
              renderNode={(child) => renderNode(child, nodeDisabled)}
              renderDropZone={
                callbacks?.renderGroupDropZone
                  ? (i) => callbacks.renderGroupDropZone!(node.id, i)
                  : undefined
              }
              gridRowAlignItems={mode === 'app' ? 'stretch' : 'center'}
            />
          ) : (
            <>
              {kids.map((child) => (
                <Fragment key={child.id}>{renderNode(child, nodeDisabled)}</Fragment>
              ))}
            </>
          )
          break
        }

        case 'Totals':
          rendered = (
            <TotalsFooter
              orientation={node.totalsOrientation ?? 'horizontal'}
              items={node.totalsItems ?? []}
              formValues={formValues ?? {}}
              preview={mode !== 'canvas'}
            />
          )
          break

        default:
          rendered = null
      }

      if (rendered && callbacks?.wrapNode) {
        return <>{callbacks.wrapNode(node, rendered)}</>
      }
      return rendered
    },
    [formValues, mode, callbacks, barRuntime, activeListActions, attributeTypes, layoutRoot, commandBarExtras],
  )

  return (
    <Box
      data-testid="form-renderer-surface"
      sx={{
        width: '100%',
        maxWidth: capContentWidth ? maxContentWidth : undefined,
        mx: capContentWidth ? 'auto' : undefined,
        ...(lockFormScroll
          ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : null),
      }}
    >
      <GridColumnsContext.Provider value={schema?.gridColumns}>
        <FormGrid
          nodes={layoutRoot}
          renderNode={renderNode}
          renderDropZone={callbacks?.renderRootDropZone}
          rowGap={denseRootStack ? 2.5 : 1}
          gridRowAlignItems={
            denseRootStack ? 'flex-start' : mode === 'app' ? 'stretch' : 'center'
          }
          fillRemaining={lockFormScroll}
        />
      </GridColumnsContext.Provider>
    </Box>
  )
}
